import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CoverPhotoUpload from "./CoverPhotoUpload";
import useIsMobile from "../../../../../../core/hooks/useMobile";
import VoiceSampleModal from "./voice-sample-modal/VoiceSampleModal";
import editIcon from "../../../../../../assets/png/edit.png";
import downloadIcon from "../../../../../../assets/png/download.png";
import printingIcon from "../../../../../../assets/png/printing-black.png";
import audiobookIcon from "../../../../../../assets/png/audiobook-purple.png";
import checkIcon from "../../../../../../assets/png/check.png";

import "./BookHeader.css";

const BookHeader = ({
  isPaid,
  bookTitle,
  bookType,
  setBookTitle,
  progressBarPercentage,
  coverPhotoInputId,
  coverPhotoImageId,
  onUploadImageClick,
  onUploadImageChange,
  onRemoveImage,
  loading,
  onClickUpdateTitle,
  onClickDownloadBook,
  onClickGenerateAudiobook,
  onClickGetPrintedBook,
  handleVoiceUploaded,
  onClickPreviewBook,
  onGenerateWithAI,
}) => {
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(bookTitle);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [isGeneratingCover, setIsGeneratingCover] = useState(false);
  const isMobile = useIsMobile();
  const inputRef = useRef(null);

  // Update tempTitle when bookTitle prop changes
  useEffect(() => {
    setTempTitle(bookTitle);
  }, [bookTitle]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e) => {
    setTempTitle(e.target.value);
  };

  const handleTitleSubmit = () => {
    if (tempTitle.trim()) {
      setBookTitle(tempTitle.trim());
    } else {
      setTempTitle(bookTitle);
    }
    onClickUpdateTitle(tempTitle.trim());
    setIsEditingTitle(false);
  };

  const handleBlur = () => {
    handleTitleSubmit();
  };

  const handleImageClick = () => {
    const hasImage = localStorage.getItem(coverPhotoImageId);
    if (hasImage) {
      setShowImagePreview(true);
    }
  };

  const handleGenerateWithAI = async () => {
    setIsGeneratingCover(true);
    try {
      await onGenerateWithAI();
    } finally {
      setIsGeneratingCover(false);
    }
  };

  return (
    <div className="row justify-content-center mb-4">
      <div className="book-header-container">
        {/* Main Header Content */}
        <div className="book-header-main">
          {/* Book Icon/Cover Photo Section */}
          <div className="book-cover-section">
            <CoverPhotoUpload
              coverPhotoInputId={coverPhotoInputId}
              coverPhotoImageId={coverPhotoImageId}
              onUploadImageClick={onUploadImageClick}
              onUploadImageChange={onUploadImageChange}
              onRemoveImage={onRemoveImage}
              onGenerateWithAI={handleGenerateWithAI}
              onImageClick={handleImageClick}
              compact={true}
              hasImage={!!localStorage.getItem(coverPhotoImageId)}
              isGenerating={isGeneratingCover}
            />
          </div>

          {/* Book Title and Description */}
          <div className="book-title-section">
            <div className="book-title-wrapper">
              {isEditingTitle ? (
                <div className="book-title-edit-wrapper">
                  <input
                    ref={inputRef}
                    type="text"
                    value={tempTitle}
                    onChange={handleTitleChange}
                    onBlur={handleBlur}
                    className="book-title-input"
                  />
                  <button
                    className="book-title-submit-btn"
                    onClick={handleTitleSubmit}
                  >
                    <img src={checkIcon} alt="Submit" width={20} height={20} />
                  </button>
                </div>
              ) : (
                <h1 onClick={handleTitleClick} className="book-title">
                  {bookTitle}
                </h1>
              )}

              {!isEditingTitle && (
                <img
                  src={editIcon}
                  alt="Edit"
                  width={24}
                  height={24}
                  onClick={handleTitleClick}
                />
              )}
            </div>
            <p className="book-description">
              {" "}
              {t(`book_type.${bookType}`) || t("other")}
            </p>
          </div>

          {/* Action Buttons - Hidden on mobile */}
          {!isMobile && (
            <div className="book-header-actions">
              <button
                title={t("download_book")}
                disabled={loading || !isPaid}
                onClick={onClickDownloadBook}
                className={!isPaid ? "book-header-btn-disabled" : ""}
              >
                <img src={downloadIcon} alt="Download" width={40} height={40} />{" "}
              </button>
              <button
                title={t("get_audio_book")}
                disabled={loading || !isPaid}
                onClick={onClickGenerateAudiobook}
                className={!isPaid ? "book-header-btn-disabled" : ""}
              >
                <img
                  src={audiobookIcon}
                  alt="Audiobook"
                  width={40}
                  height={40}
                />{" "}
              </button>
              <button
                title={t("get_printed_book")}
                disabled={loading || !isPaid}
                onClick={onClickGetPrintedBook}
                className={!isPaid ? "book-header-btn-disabled" : ""}
              >
                <img src={printingIcon} alt="Printing" width={40} height={40} />{" "}
              </button>
            </div>
          )}
        </div>

        {/* Full Width Progress Bar */}
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${progressBarPercentage}%` }}
            />
          </div>
          <div className="progress-bar-text">
            {parseInt(progressBarPercentage)}%
          </div>
        </div>

        {/* Mobile Action Buttons - Only visible on mobile */}
        {isMobile && (
          <div className="book-header-actions-mobile">
            <button
              className={`book-header-btn ${!isPaid ? "book-header-btn-disabled" : ""}`}
              title={t("download_book")}
              disabled={loading || !isPaid}
              onClick={onClickDownloadBook}
            >
              <img src={downloadIcon} alt="Download" width={36} height={36} />
            </button>
            <button
              className={`book-header-btn ${!isPaid ? "book-header-btn-disabled" : ""}`}
              title={t("get_audio_book")}
              disabled={loading || !isPaid}
              onClick={onClickGenerateAudiobook}
            >
              <img src={audiobookIcon} alt="Audiobook" width={36} height={36} />
            </button>
            <button
              className={`book-header-btn ${!isPaid ? "book-header-btn-disabled" : ""}`}
              title={t("get_printed_book")}
              disabled={loading || !isPaid}
              onClick={onClickGetPrintedBook}
            >
              <img src={printingIcon} alt="Printing" width={36} height={36} />
            </button>
          </div>
        )}

        {/* Add VoiceSampleModal */}
        <VoiceSampleModal
          id="voice-sample-modal-header"
          onUploaded={handleVoiceUploaded}
        />

        {/* Image Preview Modal */}
        {showImagePreview && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000,
              padding: "20px",
            }}
            onClick={() => setShowImagePreview(false)}
          >
            <div
              style={{
                position: "relative",
                maxWidth: "90vw",
                maxHeight: "90vh",
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "10px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setShowImagePreview(false)}
                style={{
                  position: "absolute",
                  top: "-10px",
                  right: "-10px",
                  width: "36px",
                  height: "36px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  fontSize: "20px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                  zIndex: 1,
                }}
              >
                ×
              </button>
              <img
                src={localStorage.getItem(coverPhotoImageId)}
                alt="Cover Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "85vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookHeader;
